<template>
  <b-row v-if="isTire" no-gutters class="flex-column">
    <div v-if="tireConfigurations">
      <div v-for="(tireConfiguration, tireIndex) in tireConfigurations" :key="tireIndex">
        <tire-inspection
          :ref="`tire-inspection-${sectionId}-${itemId}`"
          :item-id="itemId"
          :tire-id="tireConfiguration.tireConfigurationId"
          :readonly="readonly"
        />
      </div>
    </div>
  </b-row>
  <b-row v-else-if="isBrake" no-gutters class="flex-column">
    <b-col cols="12" class="inspection-item-name">
      {{ item.name }}
    </b-col>

    <div v-if="!brakeConfigurations || brakeConfigurations.length === 0">
      <b-button variant="outline-secondary" class="mr-2 mb-3 readonly-btn" :pressed="true">
        {{ NA }}
      </b-button>
    </div>

    <b-col v-for="brakeConfiguration in brakeConfigurations" :key="brakeConfiguration.brakeConfigurationId" cols="12">
      <brake-inspection
        :ref="`brake-inspection-${sectionId}-${itemId}`"
        :item-id="itemId"
        :brake-id="brakeConfiguration.brakeConfigurationId"
        :readonly="readonly"
      />
    </b-col>
  </b-row>
  <b-row v-else no-gutters>
    <b-col cols="12" class="inspection-item-name d-flex">
      <div v-if="isCheckbox">
        <b-form-checkbox
          :id="`section-${sectionId}-item-${itemId}-checkbox`"
          v-model="result"
          :value="checked"
          :unchecked-value="unchecked"
          :disabled="readonly"
        />
      </div>
      <div class="ml-0">
        {{ item.name }}
      </div>
    </b-col>

    <b-col cols="12" md="auto">
      <template v-if="isResultSelection">
        <b-button
          v-if="readonly"
          :id="resultControlId"
          class="mr-2 mb-3 readonly-btn"
          :pressed="true"
          :variant="buttonVariant(result)"
        >
          {{ result != null ? result.description : NA }}
        </b-button>

        <b-form-radio-group v-else :id="resultControlId" v-model="result" class="w-100 pb-2" buttons>
          <b-form-radio
            v-for="itemResult in itemResults"
            :key="itemResult.inspectionResultTypeId"
            :value="itemResult"
            :button-variant="buttonVariant(itemResult)"
          >
            {{ itemResult.description }}
          </b-form-radio>
        </b-form-radio-group>
      </template>
    </b-col>

    <b-col class="flex-fill col-md-auto pb-3">
      <template v-if="allowComment">
        <div v-if="readonly" :id="`section-${sectionId}-item-${itemId}-comment`" class="align-middle py-2">
          {{ comment }}
        </div>

        <b-textarea
          v-else
          :id="`section-${sectionId}-item-${itemId}-comment`"
          v-model="comment"
          :state="vItem.comment.$error ? false : null"
          placeholder="Comment"
        />
      </template>

      <div v-if="vItem.comment.$error && !vItem.comment.maxLength" class="error">Max character limit of 2000.</div>
    </b-col>
  </b-row>
</template>

<script>
import TireInspectionComponent from './TireInspectionComponent';
import BrakeInspectionComponent from './BrakeInspectionComponent';
import { mapGetters, mapMutations } from 'vuex';
import { LookupGetters } from '@/shared/store/lookup/types';
import { InspectionGetters, InspectionMutations } from '@/shared/store/inspection/types';

export default {
  name: 'InspectionItem',
  components: {
    'tire-inspection': TireInspectionComponent,
    'brake-inspection': BrakeInspectionComponent
  },
  props: {
    itemId: Number(),
    vItem: Object(),
    sectionId: Number(),
    readonly: Boolean()
  },
  data() {
    return {
      NA: 'N/A'
    };
  },
  computed: {
    ...mapGetters({
      getInspectionItem: InspectionGetters.GET_INSPECTION_ITEM,
      resultTypesMap: LookupGetters.GET_INSPECTION_RESULT_TYPES_MAP,
      resultTypesList: LookupGetters.GET_INSPECTION_RESULT_TYPES_LIST
    }),
    item() {
      return this.getInspectionItem(this.itemId);
    },
    itemType() {
      return (this.item.inspectionItemType || '').toLowerCase();
    },
    itemResults() {
      return this.resultTypesList.filter(result => (result.resultTypeGroup || '').toLowerCase() === this.itemType);
    },
    isResultSelection() {
      if (this.isCheckbox || this.isTire || this.isBrake) {
        return false;
      }

      return this.itemResults.length > 0 || (this.readonly && this.result != null);
    },
    resultControlId() {
      const suffix = ['passfail', 'yesno'].includes(this.itemType) ? 'pass-fail' : this.itemType;

      return `section-${this.sectionId}-item-${this.itemId}-${suffix}`;
    },
    checked() {
      return this.resultTypesMap[3];
    },
    unchecked() {
      return this.resultTypesMap[4];
    },
    isCheckbox() {
      return this.itemType === 'checkbox';
    },
    isBrake() {
      return this.itemType === 'brake';
    },
    isTire() {
      return this.itemType === 'tire';
    },
    allowComment() {
      return this.item.allowComment;
    },
    tireConfigurations() {
      return this.item.tireInspection?.tireConfigurations;
    },
    brakeConfigurations() {
      return this.item.brakeInspection?.brakeConfigurations;
    },
    result: {
      get() {
        return this.item.result;
      },
      set(value) {
        this.setItemProp('result', value);
      }
    },
    comment: {
      get() {
        return this.item.comment;
      },
      set(value) {
        this.setItemProp('comment', value);
      }
    }
  },
  methods: {
    ...mapMutations([InspectionMutations.SET_ITEM_PROP]),
    setItemProp(key, value) {
      this[InspectionMutations.SET_ITEM_PROP]({
        id: this.itemId,
        key,
        value
      });
    },
    buttonVariant(result) {
      if (result?.success === true) {
        return 'outline-success';
      }

      if (result?.success === false) {
        return 'outline-danger';
      }

      return 'outline-secondary';
    }
  }
};
</script>
